/**
 * User Management Service
 * 
 * Handles all user-related API calls including listing,
 * creating, updating, and deleting users
 */

import { api, handleApiError } from './api';
import { statusToAPI, statusFromAPI } from '@/utils';
import type {
  UserListItem,
  UserFilters,
  CreateUserPayload,
  UpdateUserPayload,
  PaginationParams,
  PaginatedResponse,
} from '@/types';

/**
 * User Service Object
 * Contains all user management API operations
 */
export const userService = {
  /**
   * Get Users List
   * Fetches paginated list of users with optional filters
   * 
   * @param params - Pagination parameters
   * @param filters - Optional filters for users
   * @returns Promise with paginated user list
   */
  getUsers: async (
    params: PaginationParams,
    filters?: UserFilters
  ): Promise<PaginatedResponse<UserListItem>> => {
    try {
      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        size: params.size.toString(),
        ...(params.sort && { sort: params.sort }),
        ...(params.search && { search: params.search }),
        ...(filters?.status && { status: filters.status }),
        ...(filters?.organizationId && { 
          organizationId: filters.organizationId.toString() 
        }),
        ...(filters?.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters?.dateTo && { dateTo: filters.dateTo }),
      });

      const response = await api.get<PaginatedResponse<UserListItem>>(
        `/users?${queryParams.toString()}`
      );

      // Log para debug (remover em produção)
      if (import.meta.env.DEV) {
        console.log('📊 Users API Response:', response.data);
      }

      // Verificar se a resposta tem o formato esperado
      if (!response.data) {
        throw new Error('Invalid response format: missing data');
      }

      // Normalizar resposta do Spring Data Pageable para nosso formato
      const responseData = response.data as any;
      
      // Se a resposta tem o formato Spring Data (content, pageable, totalPages)
      if (responseData.content && responseData.pageable) {
        // Normalizar dados dos usuários (converter status para lowercase)
        const normalizedUsers = responseData.content.map((user: any) => ({
          ...user,
          id: user.id?.toString() || user.id, // Garantir que ID é string
          status: statusFromAPI(user.status || 'INACTIVE'), // Converter status para lowercase
        }));

        return {
          data: normalizedUsers,
          pagination: {
            page: responseData.number || 0,
            size: responseData.size || params.size,
            total: responseData.totalElements || 0,
            totalPages: responseData.totalPages || 1,
            hasNext: !responseData.last,
            hasPrev: !responseData.first,
          },
        };
      }

      // Se a resposta não tiver a estrutura de paginação, criar uma
      if (!response.data.pagination) {
        const items = Array.isArray(response.data) ? response.data : [];
        return {
          data: items,
          pagination: {
            page: params.page,
            size: params.size,
            total: items.length,
            totalPages: Math.ceil(items.length / params.size) || 1,
            hasNext: params.page < Math.ceil(items.length / params.size) - 1,
            hasPrev: params.page > 0,
          },
        };
      }

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get User by ID
   * Fetches detailed information about a specific user
   * 
   * @param id - User ID
   * @returns Promise with user details
   */
  getUserById: async (id: string): Promise<UserListItem> => {
    try {
      const response = await api.get<UserListItem>(`/users/${id}`);
      
      // Normalize status from API response
      const user = response.data;
      return {
        ...user,
        status: statusFromAPI(user.status) as any,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get User by Reference (UUID)
   * Fetches detailed information about a specific user using reference
   * More secure than using ID as it doesn't expose sequential IDs
   * 
   * @param reference - User reference (UUID)
   * @returns Promise with user details
   */
  getUserByReference: async (reference: string): Promise<UserListItem> => {
    try {
      const response = await api.get<UserListItem>(`/users/reference/${reference}`);
      
      // Normalize status from API response
      const user = response.data;
      return {
        ...user,
        status: statusFromAPI(user.status) as any,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Create User
   * Creates a new user in the system
   * 
   * @param userData - User data for creation
   * @returns Promise with created user
   */
  createUser: async (userData: CreateUserPayload): Promise<UserListItem> => {
    try {
      // Convert status to uppercase for API
      const apiPayload = {
        ...userData,
        status: userData.status ? statusToAPI(userData.status) : 'ACTIVE',
      };
      
      const response = await api.post<UserListItem>('/users', apiPayload);
      
      // Normalize status from API response
      // API returns user directly, not wrapped in { user: ... }
      const user = response.data;
      return {
        ...user,
        status: statusFromAPI(user.status) as any,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Update User
   * Updates an existing user's information
   * 
   * @param id - User ID
   * @param userData - Partial user data to update
   * @returns Promise with updated user
   */
  updateUser: async (
    id: string,
    userData: UpdateUserPayload
  ): Promise<UserListItem> => {
    try {
      // Convert status to uppercase for API if provided
      const apiPayload = {
        ...userData,
        ...(userData.status && { status: statusToAPI(userData.status) }),
      };
      
      const response = await api.put<UserListItem>(
        `/users/${id}`,
        apiPayload
      );
      
      // Normalize status from API response
      // API returns user directly, not wrapped in { user: ... }
      const user = response.data;
      return {
        ...user,
        status: statusFromAPI(user.status) as any,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Delete User
   * Deletes a user from the system
   * 
   * @param id - User ID
   * @returns Promise that resolves when user is deleted
   */
  deleteUser: async (id: string): Promise<void> => {
    try {
      await api.delete(`/users/${id}`);
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Update User Status
   * Changes user status (active, inactive, suspended, pending)
   * 
   * @param id - User ID
   * @param status - New status
   * @returns Promise with updated user
   */
  updateUserStatus: async (
    id: string,
    status: string
  ): Promise<UserListItem> => {
    try {
      // Convert status to uppercase for API
      const response = await api.patch<UserListItem>(
        `/users/${id}/status`,
        { status: statusToAPI(status) }
      );
      
      // Normalize status from API response
      // API returns user directly, not wrapped in { user: ... }
      const user = response.data;
      return {
        ...user,
        status: statusFromAPI(user.status) as any,
      };
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Bulk Delete Users
   * Deletes multiple users at once
   * 
   * @param ids - Array of user IDs to delete
   * @returns Promise that resolves when users are deleted
   */
  bulkDeleteUsers: async (ids: string[]): Promise<void> => {
    try {
      await api.post('/users/bulk-delete', { ids });
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Export Users
   * Exports users list to CSV/Excel
   * 
   * @param filters - Optional filters for export
   * @returns Promise with file blob
   */
  exportUsers: async (filters?: UserFilters): Promise<Blob> => {
    try {
      const queryParams = new URLSearchParams({
        ...(filters?.status && { status: filters.status }),
        ...(filters?.organizationId && { 
          organizationId: filters.organizationId.toString() 
        }),
        ...(filters?.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters?.dateTo && { dateTo: filters.dateTo }),
      });

      const response = await api.get(
        `/users/export?${queryParams.toString()}`,
        {
          responseType: 'blob',
        }
      );

      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },
};

export default userService;
